import NodeCache from 'node-cache'
import getConfig from 'next/config'
import type { GetServerSidePropsContext } from 'next'
import { logger, nullLogger } from '@mc/next-logger'
import { redisClient, isRedisCacheEnabled } from '@mc/next-cache'
import {
  get as getCore,
  getDevAccessHeaders,
  FIXED_HEADERS_FOR_API,
} from '@mc/next-api-core'
import { getForwardedHeaders } from '../../utils/forwardHeaders'

const cacheLogger =
  process.env.CACHE_DEBUG_LOGGING === 'true' ? logger : nullLogger

const { serverRuntimeConfig } = getConfig()

const { NEXT_API_CACHE, NEXT_API_CACHE_TTL } = serverRuntimeConfig ?? {}
const CACHE_TTL = NEXT_API_CACHE_TTL || 3600

const ENV_NAME = process.env.OTEL_SERVICE_NAME || 'unknown_service'

const getHeaders = (ctx: GetServerSidePropsContext) => ({
  ...FIXED_HEADERS_FOR_API,
  ...getDevAccessHeaders(),
  ...getForwardedHeaders(ctx),
})

const cache = new NodeCache()

export const get = async <T = unknown>(
  ctx: GetServerSidePropsContext,
  path = '',
): Promise<T> => {
  const headers = getHeaders(ctx)
  return getCore(headers, path)
}

export const getWithCache = async <T>(
  ctx: GetServerSidePropsContext,
  path = '',
  { ttl = CACHE_TTL, keys = [] }: { ttl?: number; keys?: string[] } = {},
): Promise<T> => {
  const cacheKey = [path, ...keys].join('_')

  if (cache.has(cacheKey)) {
    cacheLogger.log('In-memory cache hit', { cacheKey })
    return cache.get(cacheKey) as T
  }

  cacheLogger.log('In-memory cache miss', { cacheKey })

  const t0 = performance.now()
  const response = await get<T>(ctx, path)
  const t1 = performance.now()
  cacheLogger.log('API request time', { path, time: t1 - t0 })

  if (NEXT_API_CACHE && response) {
    cache.set(cacheKey, response, ttl)
    cacheLogger.log('In-memory cache set', { cacheKey, ttl })
  }

  return response
}

/**
 * Utility to add generic data to the nextjs cache.
 * `get/post` only supports HTTP requests
 * This method invokes an arbitrary promise passed in as `getter`
 * then caches/retrieves its result
 */
export const retrieveWithCache = async <T>(
  cacheKey: string,
  getter: () => Promise<T>,
  { ttl = CACHE_TTL }: { ttl?: number } = {},
) => {
  if (cache.has(cacheKey)) {
    return cache.get(cacheKey) as T
  }
  const result = await getter()
  if (NEXT_API_CACHE && result) {
    cache.set(cacheKey, result, ttl)
  }
  return result
}

export const getWithRedisCache = async <T>(
  ctx: GetServerSidePropsContext,
  path = '',
  { ttl = CACHE_TTL, keys = [] }: { ttl?: number; keys?: string[] } = {},
): Promise<T> => {
  const isEnabled = await isRedisCacheEnabled(ctx)
  if (!isEnabled) {
    cacheLogger.log('Redis cache disabled', { path, keys })
    return getWithCache(ctx, path, { ttl, keys })
  }
  if (!redisClient) {
    cacheLogger.error('Redis client not available', { path, keys })
    return getWithCache(ctx, path, { ttl, keys })
  }

  const NS = `prepw_cache:${ENV_NAME}:api`
  const redisKey = [NS, path, ...keys].join('_')

  let cached: string | null = null
  try {
    cached = await redisClient.get(redisKey)
    cacheLogger.log('Fetched Redis key', { redisKey })
  } catch (e) {
    logger.error('Error fetching Redis key', { error: String(e) })
  }

  if (cached) {
    try {
      const parsed = JSON.parse(cached) as T
      cacheLogger.log('Returning cached Redis response', { redisKey })
      return parsed
    } catch (e) {
      logger.error('Error parsing cached Redis response', e)
    }
  } else {
    cacheLogger.log('Cache miss', { redisKey })
  }

  const response = await getWithCache<T>(ctx, path)
  try {
    await redisClient?.set(redisKey, JSON.stringify(response), 'EX', ttl)
    cacheLogger.log('Set Redis cache', { redisKey, ttl })
  } catch (e) {
    logger.error('Error setting Redis cache', { error: String(e) })
  }

  return response
}
